import React, { useState, useRef } from 'react';
import '../Connect/Connect.css';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from 'react-icons/fa';
import emailjs from "emailjs-com";
import Swal from 'sweetalert2';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useRef();

  const validateName = (name) => {
    const regex = /^[a-zA-Z\s]*$/;
    if (!name) return 'Name is required';
    if (!regex.test(name)) return 'Only alphabets are allowed';
    return '';
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!regex.test(email)) return 'Please enter a valid email';
    return '';
  };

  const validatePhone = (phone) => {
    const regex = /^\d{10}$/;
    if (!phone) return 'Phone number is required';
    if (!regex.test(phone)) return 'Please enter a valid 10-digit phone number';
    return '';
  };

  const validateSubject = (subject) => {
    if (!subject) return 'Subject is required';
    return '';
  };

  const validateMessage = (message) => {
    if (!message) return 'Message is required';
    if (message.length < 15) return 'Message must be at least 15 characters';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Real-time validation
    let error = '';
    switch (name) {
      case 'name':
        error = validateName(value);
        break;
      case 'email':
        error = validateEmail(value);
        break;
      case 'phone':
        error = validatePhone(value);
        break;
      case 'subject':
        error = validateSubject(value);
        break;
      case 'message':
        error = validateMessage(value);
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateName(formData.name),
      email: validateEmail(formData.email),
      phone: validatePhone(formData.phone),
      subject: validateSubject(formData.subject),
      message: validateMessage(formData.message)
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const showSuccessAlert = () => {
    Swal.fire({
      title: '<strong>Thank You!</strong>',
      html: `
        <div style="text-align: center;">
          <p style="font-size: 18px; margin-bottom: 20px;">
            Your message has been successfully sent to <strong>श्री Var Event & Conference</strong>
          </p>
          <p style="font-size: 16px;">
            We'll get back to you soon.
          </p>
        </div>
      `,
      icon: 'success',
      confirmButtonText: 'Okay',
      confirmButtonColor: '#cb4531',
      background: '#fff',
      backdrop: `
        rgba(0,0,0,0.7)
        url("/images/party-popper.gif")
        center top
        no-repeat
      `,
      showClass: {
        popup: 'animate__animated animate__fadeInDown'
      },
      hideClass: {
        popup: 'animate__animated animate__fadeOutUp'
      }
    });
  };

  const showErrorAlert = () => {
    Swal.fire({
      title: 'Error!',
      text: 'Failed to send the message, please try again.',
      icon: 'error',
      confirmButtonText: 'Try Again',
      confirmButtonColor: '#cb4531'
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      emailjs.sendForm(
        'service_c2jf13w', 
        'template_c34kf4z', 
        form.current,
        'UxPRjy8Ztmqt2j8Ls' 
      )
      .then((result) => {
          console.log(result.text);
          showSuccessAlert();
          setFormData({
            name: '',
            email: '',
            phone: '',
            subject: '',
            message: ''
          });
      }, (error) => {
          console.log(error.text);
          showErrorAlert();
      })
      .finally(() => {
          setIsSubmitting(false);
      });
    }
  };

  const isFormValid = () => {
    return (
      formData.name &&
      formData.email &&
      formData.phone &&
      formData.subject &&
      formData.message.length >= 15 &&
      !errors.name &&
      !errors.email &&
      !errors.phone &&
      !errors.subject &&
      !errors.message
    );
  };

  return (
    <section className="contact-section">
      <div className="contact-heading">
        <h2>
          <span className="highlight-pink">We Are </span>
          <span className="highlight-purple">Here To Help You!</span>
        </h2>
        <p className="subtext">We will make your next celebration very special</p>
      </div>

      <div className="contact-main">
        <form className="contact-form" ref={form} onSubmit={handleSubmit}>
          <div className="form-group">
            <input 
              type="text" 
              name="name" 
              placeholder="Your name" 
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && <div className="error-msg">{errors.name}</div>}
          </div>
          
          <div className="form-group">
            <input 
              type="email" 
              name="email" 
              placeholder="Your email" 
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && <div className="error-msg">{errors.email}</div>}
          </div>

          <div className="form-group">
            <input 
              type="tel" 
              name="phone" 
              placeholder="Phone number" 
              value={formData.phone}
              onChange={handleChange}
              maxLength="10"
            />
            {errors.phone && <div className="error-msg">{errors.phone}</div>}
          </div>
          
          <div className="form-group">
            <input 
              type="text" 
              name="subject" 
              placeholder="Subject" 
              value={formData.subject}
              onChange={handleChange}
            />
            {errors.subject && <div className="error-msg">{errors.subject}</div>}
          </div>
          
          <div className="form-group">
            <textarea 
              rows="5" 
              name="message" 
              placeholder="Your message"
              value={formData.message}
              onChange={handleChange}
            ></textarea>
            {errors.message && <div className="error-msg">{errors.message}</div>}
          </div>
          
          <button 
            type="submit" 
            disabled={!isFormValid() || isSubmitting}
            className={isSubmitting ? 'submitting' : ''}
          >
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Sending...
              </>
            ) : (
              'Send Now'
            )}
          </button>
        </form>

        <div className="contact-info">
          <img src="/shree.png" alt="Event Logo" loading="lazy" className="contact-logo" />
          <h3>Event <span>Planners</span></h3>

          <div className="info-item">
            <FaMapMarkerAlt className="iconc" />
            <p>Sec-62, Noida, Uttar Pradesh, India</p>
          </div>
          <div className="info-item">
            <FaPhoneAlt className="iconc" />
            <p>+91 922-034-7091</p>
          </div>
          <div className="info-item">
            <FaEnvelope className="iconc" />
            <p>info@shreeverevents.com</p>
          </div>
          <div className="info-item">
            <FaClock className="iconc" />
            <p>24x7 Services</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;